import React, { useCallback, useEffect, useState } from 'react';
import Layout from './Layout';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';
import { borrarRondaLocal, guardarRondaLocal, leerRondaLocal } from '../utils/rondaOfflineStorage';
import {
  abrirPantallaRondaLocal,
  descargarPlanAndroid,
  leerPlanAndroid,
  puenteAndroid
} from '../utils/rondaAndroidBridge';

const PASOS_RONDA = [
  {
    titulo: 'Tenga internet e inicie',
    detalle: 'Pulse Iniciar ronda y confirme. Se descarga el plan del puesto.'
  },
  {
    titulo: 'Recorra los puntos',
    detalle: 'Revise cada punto del sitio. Si tiene verificación, responda sí o no y escriba la novedad.'
  },
  {
    titulo: 'Vuelva a un lugar con internet',
    detalle: 'Al terminar, regrese a donde haya señal para enviar los registros guardados en el teléfono.'
  },
  {
    titulo: 'Finalice la ronda',
    detalle: 'Pulse Finalizar ronda. Se envían las lecturas y se cierra el modo ronda.'
  }
];

const ModalConfirmacion = ({ titulo, texto, etiquetaSi, etiquetaNo, onSi, onNo, ocupado }) => (
  <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4 bg-black/50" role="dialog" aria-modal="true">
    <div className="bg-white w-full max-w-md rounded-2xl shadow-xl overflow-hidden">
      <div className="px-5 pt-5 pb-2">
        <h2 className="text-lg font-semibold text-gray-900">{titulo}</h2>
        <p className="mt-2 text-sm text-gray-600 leading-relaxed">{texto}</p>
      </div>
      <div className="px-5 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
        <button
          type="button"
          onClick={onNo}
          disabled={ocupado}
          className="w-full sm:w-auto px-4 py-2.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        >
          {etiquetaNo}
        </button>
        <button
          type="button"
          onClick={onSi}
          disabled={ocupado}
          className="w-full sm:w-auto px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60"
        >
          {ocupado ? 'Iniciando…' : etiquetaSi}
        </button>
      </div>
    </div>
  </div>
);

const RondaOffline = () => {
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [pedirInicio, setPedirInicio] = useState(false);
  const [pedirQuitar, setPedirQuitar] = useState(false);

  const username = user?.username || '';
  const idGenerado = user?.id_generado;
  const enApp = Boolean(puenteAndroid());

  const irAPantallaLocal = useCallback(() => {
    if (abrirPantallaRondaLocal()) return;
    setError('La aplicación no abrió la pantalla de ronda.');
  }, []);

  useEffect(() => {
    window.onPlanDescargado = (exito, mensaje) => {
      setTrabajando(false);
      setPedirInicio(false);
      if (!exito) {
        setError(mensaje || 'No se pudo descargar el plan.');
        return;
      }
      setError('');
      irAPantallaLocal();
    };

    if (enApp) {
      setPlan(leerPlanAndroid());
      setCargando(false);
    } else if (username && idGenerado) {
      setPlan(leerRondaLocal(username, idGenerado));
      setCargando(false);
    } else {
      setCargando(false);
    }

    return () => {
      delete window.onPlanDescargado;
    };
  }, [enApp, username, idGenerado, irAPantallaLocal]);

  const iniciar = async () => {
    setError('');
    setAviso('');
    setTrabajando(true);
    if (enApp && descargarPlanAndroid(idGenerado, username)) {
      return;
    }
    try {
      const response = await fetch(getApiUrl('RONDA_OFFLINE', 'INICIAR'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ idGenerado, username })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        setError(data.error || 'No se pudo iniciar la ronda.');
        return;
      }
      guardarRondaLocal(username, idGenerado, data.data);
      setPlan(data.data);
      setAviso('Plan descargado. En el teléfono, la ronda se hace en la pantalla local.');
      setPedirInicio(false);
    } catch {
      setError('Sin conexión. Iniciar ronda necesita internet para descargar el plan.');
    } finally {
      setTrabajando(false);
    }
  };

  const cancelar = () => {
    borrarRondaLocal(username, idGenerado);
    setPlan(null);
    setError('');
    setAviso('Plan quitado de este navegador.');
  };

  const marcas = plan?.marcas || [];

  return (
    <Layout>
      <div className="max-w-lg mx-auto px-4 py-5">
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-5 pt-5 pb-4 border-b border-gray-100">
            <h1 className="text-xl font-semibold text-gray-900">Ronda</h1>
            <p className="text-sm text-gray-500 mt-1">Siga estos pasos para recorrer el puesto y enviar los registros.</p>
          </div>

          <ol className="px-5 py-4 space-y-4">
            {PASOS_RONDA.map((paso, index) => (
              <li key={paso.titulo} className="flex gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-white text-sm font-semibold">
                  {index + 1}
                </span>
                <div className="pt-0.5">
                  <p className="text-sm font-semibold text-gray-900">{paso.titulo}</p>
                  <p className="text-sm text-gray-600 mt-0.5 leading-relaxed">{paso.detalle}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mx-5 mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm font-semibold text-amber-900">Modo ronda</p>
            <p className="text-sm text-amber-800 mt-1 leading-relaxed">
              Al confirmar el inicio se activa el modo ronda. Mientras esté en curso no podrá usar las demás opciones de la aplicación. Termine los puntos y pulse Finalizar ronda para volver al menú.
            </p>
          </div>

          {error ? (
            <div className="mx-5 mb-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>
          ) : null}
          {aviso ? (
            <div className="mx-5 mb-3 rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">{aviso}</div>
          ) : null}

          <div className="px-5 pb-5 space-y-2">
            {cargando ? (
              <p className="text-sm text-gray-500">Cargando…</p>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setError('');
                    setPedirInicio(true);
                  }}
                  disabled={trabajando || !idGenerado}
                  className="w-full px-4 py-3 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60"
                >
                  {trabajando ? 'Descargando plan…' : 'Iniciar ronda'}
                </button>
                {enApp && plan ? (
                  <button
                    type="button"
                    onClick={irAPantallaLocal}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-300 text-sm font-medium text-gray-800 hover:bg-gray-50"
                  >
                    Abrir pantalla de ronda
                  </button>
                ) : null}
                {!enApp && plan ? (
                  <button
                    type="button"
                    onClick={() => setPedirQuitar(true)}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Quitar plan de este navegador
                  </button>
                ) : null}
              </>
            )}
          </div>
        </div>

        {!enApp && marcas.length ? (
          <div className="mt-4 space-y-2">
            <p className="text-sm font-medium text-gray-700">{marcas.length} puntos en el plan. La lectura se hace en el teléfono.</p>
            {marcas.map((marca) => (
              <div key={marca.id} className="bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-sm">
                <p className="text-sm font-semibold text-gray-900">{marca.numero}. {marca.nombre}</p>
                <p className="text-xs text-gray-500">{String(marca.tipo).toUpperCase() === 'GPS' ? 'GPS' : 'Tag'}</p>
                {Array.isArray(marca.consignas) && marca.consignas.length ? (
                  <ul className="mt-2 space-y-1">
                    {marca.consignas.map((consigna) => (
                      <li key={consigna.id_checks} className="text-xs text-gray-600">
                        {consigna.descripcion || `Check ${consigna.id_checks}`}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {pedirQuitar ? (
        <ModalConfirmacion
          titulo="¿Quitar el plan descargado?"
          texto="Se borra el plan de este navegador. No envía las lecturas."
          etiquetaSi="Sí, quitar"
          etiquetaNo="No"
          onNo={() => setPedirQuitar(false)}
          onSi={() => {
            setPedirQuitar(false);
            cancelar();
          }}
        />
      ) : null}

      {pedirInicio ? (
        <ModalConfirmacion
          titulo="¿Seguro de iniciar la ronda?"
          texto="Se descargará el plan y se activará el modo ronda. Mientras esté activo no podrá usar las demás opciones de la aplicación."
          etiquetaSi="Sí, iniciar"
          etiquetaNo="No"
          ocupado={trabajando}
          onNo={() => {
            if (trabajando) return;
            setPedirInicio(false);
          }}
          onSi={iniciar}
        />
      ) : null}
    </Layout>
  );
};

export default RondaOffline;
